import {
  makeStyles,
  Button,
  Collapse,
  FilePreview,
  FormSectionTitle,
  Grid,
  Typography,
  useMediaQuery
} from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import { useClientOptions } from '@paintscout/ui-business'
import type { FeatureProduct } from '@paintscout/util/builder'
import { getQuoteFeatureProducts } from '@paintscout/util/builder'
import classNames from 'classnames'
import React, { useContext, useState } from 'react'
import { QuoteContext } from '../../../../context/QuoteContext'
import ItemTable, { ItemTableCell } from '../../../../ItemTable'

const paintCanSize = 72

const useStyles = makeStyles<Theme, ProductsTableProps>(
  (theme) => ({
    root: {},
    tableHead: {
      background: theme.paintscout.palette.brand.Neutral.P94
    },
    headerCell: {
      color: theme.paintscout.palette.brand.Neutral.P35
    },
    filePreview: {
      maxWidth: paintCanSize,
      minWidth: paintCanSize,
      minHeight: paintCanSize,
      [theme.breakpoints.down('xs')]: {
        maxWidth: paintCanSize,
        minWidth: paintCanSize,
        minHeight: paintCanSize,
        marginLeft: theme.spacing(-1)
      }
    },
    productDetails: {
      '&$productDetails': {
        paddingLeft: theme.spacing(4),
        [theme.breakpoints.down('xs')]: {
          paddingLeft: theme.spacing(1)
        }
      }
    },
    productLabel: {
      '&$productLabel': {
        color: theme.paintscout.palette.brand.Neutral.P35,
        marginBottom: theme.spacing(1)
      }
    },
    viewMoreContainer: {
      marginTop: theme.spacing(2),
      textAlign: 'center'
    }
  }),
  { name: 'ProductsTable' }
)

export interface ProductsTableProps {
  classes?: StyleClasses<typeof useStyles>
  collapsed?: boolean
  inPresentation?: boolean
  onToggle: (ev: React.MouseEvent) => any
}

function ProductsTable(props: ProductsTableProps) {
  const classes = useStyles(props)
  const { collapsed, onToggle, inPresentation } = props
  const { quote } = useContext(QuoteContext)
  const { options } = useClientOptions()
  const isXs = useMediaQuery('xs')
  const items = getQuoteFeatureProducts({ quote, options })
  const show = !collapsed && items.length > 0
  const hidePrint = items.length === 0 || collapsed

  const [showAll, setShowAll] = useState(false)
  const visibleItems = showAll ? items : items.slice(0, 3)

  const SherwinWilliamsLogo = isXs ? (
    <img
      src={'https://paintscout.s3.us-east-1.amazonaws.com/partners/sw/images/s-w-icon.png'}
      alt={'Sherwin-Williams Logo'}
      style={{ height: 36, position: 'absolute', top: 4, right: 15 }}
    />
  ) : (
    <img
      src={'https://res.cloudinary.com/taptapquote/image/upload/v1751660780/paintscout/s-w-logo.png'}
      alt={'Sherwin-Williams Logo'}
      style={{ height: 36, position: 'absolute', top: 6, right: 8 }}
    />
  )

  if (!show && inPresentation) {
    return null
  }

  return (
    <div className={classNames({ [classes.root]: true, [classes.hidePrint]: hidePrint })}>
      <FormSectionTitle title={'Products'} showToggle={!inPresentation} toggleValue={!collapsed} onToggle={onToggle} />
      <Collapse show={show}>
        <ItemTable
          draggable={false}
          classes={{
            tableHead: classes.tableHead
          }}
          items={visibleItems}
          checkboxes={false}
          showAddButton={false}
          onItemClick={null}
          renderHeadColumns={() => (
            <div style={{ position: 'relative' }}>
              <ItemTableCell className={classes.headerCell}>Sherwin-Williams</ItemTableCell>
              <ItemTableCell className={classes.headerCell}>{SherwinWilliamsLogo}</ItemTableCell>
            </div>
          )}
          renderRowColumns={(item: FeatureProduct) => {
            return (
              <>
                <ItemTableCell colSpan={2}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={'auto'}>
                      <FilePreview
                        classes={{
                          root: classes.filePreview
                        }}
                        file={{
                          ...item.featureImage,
                          src: item?.featureImage?.value ?? null,
                          // Partner feature-product images are hosted in the legacy `paintscout`
                          // S3 bucket, which does not return Access-Control-Allow-Origin. FilePreview
                          // otherwise loads file.src with crossOrigin="anonymous" (for the Konva
                          // editor's tainted-canvas avoidance), which fails the CORS check and renders
                          // a blank image. These images are display-only — never loaded into the
                          // editor — so flagging them as provider-hosted opts them out of crossOrigin.
                          provider: 'sherwin-williams',
                          visibility: 'visible'
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm className={classes.productDetails}>
                      <Typography variant="h3" className={classes.productLabel}>
                        {item.label}
                      </Typography>
                      <Typography variant="body1">{item.description}</Typography>
                    </Grid>
                  </Grid>
                </ItemTableCell>
              </>
            )
          }}
          {...props}
        />

        {items.length > 3 && (
          <div className={classes.viewMoreContainer}>
            <Button variant={'text'} onClick={() => setShowAll((prev) => !prev)}>
              {showAll ? 'View Less' : 'View More'}
            </Button>
          </div>
        )}
      </Collapse>
    </div>
  )
}

export default ProductsTable
